import { formatNameLastFirst, formatPhoneNumber } from '@strata/helper-functions';
import { ReasonInput } from '@strata/react-compound-inputs';
import { DialogWithForm } from '@strata/react-dialog';
import { DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { SuccessToast } from '@strata/react-toast-message';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { getPrescriberFaxTypeForIntakeCase } from 'services/helpers';
import { type IntakeCase, usePostSendFaxMutation } from 'store/api/intakeCaseApi';

const ValidationSchema = Yup.object({
    ...ReasonInput.validationSchema,
});

const SendFaxDialogLabel = {
    initiation: 'Send prior auth initiation request fax.',
    notification: 'Send prior auth notification fax.',
} as const;

interface SendFaxDialogProps {
    isOpen: boolean;
    onClose: () => void;
    intakeCase: Pick<
        IntakeCase,
        | 'prescriberPracticeName'
        | 'prescriberLastName'
        | 'prescriberFirstName'
        | 'prescriberFaxNumber'
        | 'intakeCaseId'
        | 'intakeStatus'
        | 'prescriberVerified'
        | 'monitoringCase'
    >;
}

export const SendFaxDialog = ({ isOpen, onClose, intakeCase }: SendFaxDialogProps) => {
    const [postSendFax] = usePostSendFaxMutation();
    const faxType = getPrescriberFaxTypeForIntakeCase(intakeCase.monitoringCase);
    const faxLabel = intakeCase.monitoringCase
        ? SendFaxDialogLabel.notification
        : SendFaxDialogLabel.initiation;

    return (
        <Formik
            initialValues={ReasonInput.initialValues}
            validationSchema={ValidationSchema}
            enableReinitialize
            onSubmit={async ({ ...rest }, { resetForm }) => {
                await postSendFax({
                    intakeCaseId: intakeCase.intakeCaseId,
                    faxType,
                    ...ReasonInput.submitValues(rest),
                }).unwrap();
                SuccessToast({ message: 'Fax queued successfully.' });
                resetForm();
                onClose();
            }}
        >
            {(formik) => (
                <DialogWithForm
                    width="650px"
                    isOpen={isOpen}
                    dialogTitle="Send Fax"
                    isPrimaryButtonDisabled={
                        !(formik.dirty && formik.isValid) ||
                        formik.isSubmitting ||
                        !intakeCase.prescriberFaxNumber
                    }
                    onSecondaryButtonClick={() => {
                        onClose();
                        formik.resetForm();
                    }}
                    primaryButtonLabel="Send Fax"
                    secondaryButtonLabel="Cancel"
                    dialogContent={
                        <div className="sds_send-fax-dialog">
                            <div className="sds_send-fax-dialog__label">{faxLabel}</div>
                            <div className="sds_send-fax-dialog__details">
                                <DetailLabelValuePair
                                    label="Prescriber"
                                    value={
                                        intakeCase.prescriberPracticeName ??
                                        formatNameLastFirst(
                                            intakeCase.prescriberLastName,
                                            intakeCase.prescriberFirstName,
                                        )
                                    }
                                />
                                <DetailLabelValuePair
                                    label="Fax Number"
                                    value={formatPhoneNumber(intakeCase.prescriberFaxNumber)}
                                />
                            </div>
                            <ReasonInput
                                reasons={[
                                    "Didn't Receive Last Fax",
                                    'Incorrect Fax Number',
                                    'Last Fax Failed',
                                    'Prescriber Updated',
                                ]}
                            />
                        </div>
                    }
                />
            )}
        </Formik>
    );
};
